/* eslint-disable @typescript-eslint/no-explicit-any */
import { Textbox } from 'fabric';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

/**
 * Получает активный цвет текста или выделенной части текста
 * @param textObject - Текстовый объект Fabric.js
 * @returns Цвет текста как строка или массив цветов если разные части имеют разные цвета
 */
export const getActiveTextColor = (textObject: Textbox | TextEntity): string | string[] => {
  // Если объект не является текстом, возвращаем пустое значение
  if (
    !textObject ||
    (textObject.type.toLowerCase() !== 'textbox' && textObject.type.toLowerCase() !== 'textentity')
  ) {
    return '';
  }

  // Проверяем, есть ли выделение
  const hasSelection = textObject.selectionStart !== textObject.selectionEnd;

  // Если есть стили в формате массива объектов со свойствами start, end, style
  if (Array.isArray(textObject.styles)) {
    // Если есть выделение, ищем стили, которые пересекаются с выделением
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      const colorsInSelection = new Set<string>();

      textObject.styles.forEach((styleItem: any) => {
        // Проверяем, пересекается ли стиль с выделением
        if (
          styleItem.style &&
          styleItem.style.fill &&
          ((styleItem.start <= selectionStart && styleItem.end > selectionStart) ||
            (styleItem.start >= selectionStart && styleItem.start < selectionEnd))
        ) {
          colorsInSelection.add(styleItem.style.fill.toString());
        }
      });

      // Если в выделении нет цветов из стилей, используем общий цвет
      if (colorsInSelection.size === 0) {
        const fill = textObject.get('fill');
        return fill ? fill.toString() : '';
      }

      // Если найден только один цвет, возвращаем его
      if (colorsInSelection.size === 1) {
        return Array.from(colorsInSelection)[0];
      }

      // Иначе возвращаем массив цветов
      return Array.from(colorsInSelection);
    }

    // Если нет выделения, проверяем, сколько разных цветов есть в тексте
    const uniqueColors = new Set<string>();

    textObject.styles.forEach((styleItem: any) => {
      if (styleItem.style && styleItem.style.fill) {
        uniqueColors.add(styleItem.style.fill.toString());
      }
    });

    // Если нет цветов в стилях или только один цвет, возвращаем общий цвет
    if (uniqueColors.size <= 1) {
      // Если единственный цвет в стилях совпадает с общим цветом или стилей нет,
      // возвращаем общий цвет
      const fill = textObject.get('fill');
      const fillColor = fill ? fill.toString() : '';
      return uniqueColors.size === 0 ? fillColor : Array.from(uniqueColors)[0];
    }

    // Если разные части текста имеют разные цвета, возвращаем массив
    return Array.from(uniqueColors);
  }

  // Проверяем стили в формате Fabric.js (obj.styles[line][char])
  if (textObject.styles && typeof textObject.styles === 'object') {
    const uniqueColors = new Set<string>();
    let hasStyles = false;

    // Если есть выделение, проверяем только стили в выделении
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      let lineIndex = 0;
      let charIndex = 0;
      let currentPos = 0;

      // Проходим по всем строкам и символам, чтобы найти стили в выделении
      for (const lineNum in textObject.styles) {
        lineIndex = parseInt(lineNum, 10);
        const line = textObject.styles[lineNum];

        for (const charNum in line) {
          charIndex = parseInt(charNum, 10);
          currentPos = lineIndex * textObject._textLines[lineIndex].length + charIndex;

          // Если символ в пределах выделения
          if (currentPos >= selectionStart && currentPos < selectionEnd) {
            const style = line[charNum];
            if (style && style.fill) {
              uniqueColors.add(style.fill.toString());
              hasStyles = true;
            }
          }
        }
      }

      // Если в выделении нет цветов из стилей, используем общий цвет
      if (!hasStyles) {
        const fill = textObject.get('fill');
        return fill ? fill.toString() : '';
      }

      // Если найден только один цвет, возвращаем его
      if (uniqueColors.size === 1) {
        return Array.from(uniqueColors)[0];
      }

      // Иначе возвращаем массив цветов
      return Array.from(uniqueColors);
    }

    // Если нет выделения, проверяем все стили
    for (const lineNum in textObject.styles) {
      const line = textObject.styles[lineNum];
      for (const charNum in line) {
        const style = line[charNum];
        if (style && style.fill) {
          uniqueColors.add(style.fill.toString());
          hasStyles = true;
        }
      }
    }

    // Если нет стилей или только один цвет, возвращаем общий цвет
    if (!hasStyles || uniqueColors.size <= 1) {
      const fill = textObject.get('fill');
      const fillColor = fill ? fill.toString() : '';
      return uniqueColors.size === 0 ? fillColor : Array.from(uniqueColors)[0];
    }

    // Если разные части текста имеют разные цвета, возвращаем массив
    return Array.from(uniqueColors);
  }

  // Если нет стилей, возвращаем общий цвет
  const fill = textObject.get('fill');
  return fill ? fill.toString() : '';
};

/**
 * Проверяет, является ли текст или выделенная часть текста подчеркнутой
 * @param textObject Текстовый объект Fabric.js
 * @returns true, если текст подчеркнут, false - если не подчеркнут, undefined - если текст содержит части с разными стилями
 */
export const isTextUnderlined = (textObject: Textbox | TextEntity): boolean | undefined => {
  // Если объект не является текстом, возвращаем false
  if (
    !textObject ||
    (textObject.type.toLowerCase() !== 'textbox' && textObject.type.toLowerCase() !== 'textentity')
  ) {
    return false;
  }

  // Проверяем, есть ли выделение
  const hasSelection = textObject.selectionStart !== textObject.selectionEnd;

  // Если есть стили в формате массива объектов со свойствами start, end, style
  if (Array.isArray(textObject.styles)) {
    // Если есть выделение, проверяем только стили в выделении
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      const underlineStates = new Set<boolean>();
      let hasStylesInSelection = false;

      textObject.styles.forEach((styleItem: any) => {
        // Проверяем, пересекается ли стиль с выделением
        if (
          styleItem.style &&
          ((styleItem.start <= selectionStart && styleItem.end > selectionStart) ||
            (styleItem.start >= selectionStart && styleItem.start < selectionEnd))
        ) {
          if (styleItem.style.underline !== undefined) {
            underlineStates.add(!!styleItem.style.underline);
            hasStylesInSelection = true;
          }
        }
      });

      // Если нет стилей в выделении, используем общий стиль
      if (!hasStylesInSelection) {
        return !!textObject.underline;
      }

      // Если все части выделения имеют одинаковый стиль подчеркивания
      if (underlineStates.size === 1) {
        return Array.from(underlineStates)[0];
      }

      // Если разные части выделения имеют разные стили подчеркивания
      return undefined;
    }

    // Если нет выделения, проверяем все стили
    const underlineStates = new Set<boolean>();
    let hasStyles = false;

    textObject.styles.forEach((styleItem: any) => {
      if (styleItem.style && styleItem.style.underline !== undefined) {
        underlineStates.add(!!styleItem.style.underline);
        hasStyles = true;
      }
    });

    // Если нет специфических стилей, используем общий стиль
    if (!hasStyles) {
      return !!textObject.underline;
    }

    // Если все части текста имеют одинаковый стиль подчеркивания
    if (underlineStates.size === 1) {
      return Array.from(underlineStates)[0];
    }

    // Если разные части текста имеют разные стили подчеркивания
    return undefined;
  }

  // Проверяем стили в формате Fabric.js (obj.styles[line][char])
  if (textObject.styles && typeof textObject.styles === 'object') {
    const underlineStates = new Set<boolean>();
    let hasStyles = false;

    // Если есть выделение, проверяем только стили в выделении
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      let lineIndex = 0;
      let charIndex = 0;
      let currentPos = 0;

      // Проходим по всем строкам и символам, чтобы найти стили в выделении
      for (const lineNum in textObject.styles) {
        lineIndex = parseInt(lineNum, 10);
        const line = textObject.styles[lineNum];

        for (const charNum in line) {
          charIndex = parseInt(charNum, 10);
          currentPos = lineIndex * textObject._textLines[lineIndex].length + charIndex;

          // Если символ в пределах выделения
          if (currentPos >= selectionStart && currentPos < selectionEnd) {
            const style = line[charNum];
            if (style && style.underline !== undefined) {
              underlineStates.add(!!style.underline);
              hasStyles = true;
            }
          }
        }
      }

      // Если в выделении нет стилей, используем общий стиль
      if (!hasStyles) {
        return !!textObject.underline;
      }

      // Если все части выделения имеют одинаковый стиль подчеркивания
      if (underlineStates.size === 1) {
        return Array.from(underlineStates)[0];
      }

      // Если разные части выделения имеют разные стили подчеркивания
      return undefined;
    }

    // Если нет выделения, проверяем все стили
    for (const lineNum in textObject.styles) {
      const line = textObject.styles[lineNum];
      for (const charNum in line) {
        const style = line[charNum];
        if (style && style.underline !== undefined) {
          underlineStates.add(!!style.underline);
          hasStyles = true;
        }
      }
    }

    // Если нет специфических стилей, используем общий стиль
    if (!hasStyles) {
      return !!textObject.underline;
    }

    // Если все части текста имеют одинаковый стиль подчеркивания
    if (underlineStates.size === 1) {
      return Array.from(underlineStates)[0];
    }

    // Если разные части текста имеют разные стили подчеркивания
    return undefined;
  }

  // Если нет стилей, возвращаем общий стиль
  return !!textObject.underline;
};

/**
 * Проверяет, является ли текст или выделенная часть текста жирным
 * @param textObject Текстовый объект Fabric.js
 * @returns true, если текст жирный, false - если не жирный, undefined - если текст содержит части с разными стилями
 */
export const isTextBold = (textObject: Textbox | TextEntity): boolean | undefined => {
  // Если объект не является текстом, возвращаем false
  if (
    !textObject ||
    (textObject.type.toLowerCase() !== 'textbox' && textObject.type.toLowerCase() !== 'textentity')
  ) {
    return false;
  }

  const isBold = (fontWeight: any) => {
    return fontWeight === 'bold' || fontWeight >= 500;
  };

  // Проверяем, есть ли выделение
  const hasSelection = textObject.selectionStart !== textObject.selectionEnd;

  // Если есть стили в формате массива объектов со свойствами start, end, style
  if (Array.isArray(textObject.styles)) {
    // Если есть выделение, проверяем только стили в выделении
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      const boldStates = new Set<boolean>();
      let hasStylesInSelection = false;

      textObject.styles.forEach((styleItem: any) => {
        // Проверяем, пересекается ли стиль с выделением
        if (
          styleItem.style &&
          ((styleItem.start <= selectionStart && styleItem.end > selectionStart) ||
            (styleItem.start >= selectionStart && styleItem.start < selectionEnd))
        ) {
          if (styleItem.style.fontWeight !== undefined) {
            boldStates.add(isBold(styleItem.style.fontWeight));
            hasStylesInSelection = true;
          }
        }
      });

      // Если нет стилей в выделении, используем общий стиль
      if (!hasStylesInSelection) {
        return isBold(textObject.fontWeight);
      }

      // Если все части выделения имеют одинаковый стиль жирности
      if (boldStates.size === 1) {
        return Array.from(boldStates)[0];
      }

      // Если разные части выделения имеют разные стили жирности
      return undefined;
    }

    // Если нет выделения, проверяем все стили
    const boldStates = new Set<boolean>();
    let hasStyles = false;

    textObject.styles.forEach((styleItem: any) => {
      if (styleItem.style && styleItem.style.fontWeight !== undefined) {
        boldStates.add(isBold(styleItem.style.fontWeight));
        hasStyles = true;
      }
    });

    // Если нет специфических стилей, используем общий стиль
    if (!hasStyles) {
      return isBold(textObject.fontWeight);
    }

    // Если все части текста имеют одинаковый стиль жирности
    if (boldStates.size === 1) {
      return Array.from(boldStates)[0];
    }

    // Если разные части текста имеют разные стили жирности
    return undefined;
  }

  // Проверяем стили в формате Fabric.js (obj.styles[line][char])
  if (textObject.styles && typeof textObject.styles === 'object') {
    const boldStates = new Set<boolean>();
    let hasStyles = false;

    // Если есть выделение, проверяем только стили в выделении
    if (hasSelection) {
      const selectionStart = textObject.selectionStart;
      const selectionEnd = textObject.selectionEnd;

      let lineIndex = 0;
      let charIndex = 0;
      let currentPos = 0;

      // Проходим по всем строкам и символам, чтобы найти стили в выделении
      for (const lineNum in textObject.styles) {
        lineIndex = parseInt(lineNum, 10);
        const line = textObject.styles[lineNum];

        for (const charNum in line) {
          charIndex = parseInt(charNum, 10);
          currentPos = lineIndex * textObject._textLines[lineIndex].length + charIndex;

          // Если символ в пределах выделения
          if (currentPos >= selectionStart && currentPos < selectionEnd) {
            const style = line[charNum];
            if (style && style.fontWeight !== undefined) {
              boldStates.add(isBold(style.fontWeight));
              hasStyles = true;
            }
          }
        }
      }

      // Если в выделении нет стилей, используем общий стиль
      if (!hasStyles) {
        return isBold(textObject.fontWeight);
      }

      // Если все части выделения имеют одинаковый стиль жирности
      if (boldStates.size === 1) {
        return Array.from(boldStates)[0];
      }

      // Если разные части выделения имеют разные стили жирности
      return undefined;
    }

    // Если нет выделения, проверяем все стили
    for (const lineNum in textObject.styles) {
      const line = textObject.styles[lineNum];
      for (const charNum in line) {
        const style = line[charNum];
        if (style && style.fontWeight !== undefined) {
          boldStates.add(isBold(style.fontWeight));
          hasStyles = true;
        }
      }
    }

    // Если нет специфических стилей, используем общий стиль
    if (!hasStyles) {
      return isBold(textObject.fontWeight);
    }

    // Если все части текста имеют одинаковый стиль жирности
    if (boldStates.size === 1) {
      return Array.from(boldStates)[0];
    }

    // Если разные части текста имеют разные стили жирности
    return undefined;
  }

  // Если нет стилей, возвращаем общий стиль
  return isBold(textObject.fontWeight);
};

/**
 * Проверяет, является ли текст или выделенная часть текста обычным (не жирным и не подчеркнутым)
 * @param textObject Текстовый объект Fabric.js
 * @returns true, если текст обычный, false - если не обычный, undefined - если текст содержит части с разными стилями
 */
export const isTextRegular = (textObject: Textbox | TextEntity): boolean | undefined => {
  // Если объект не является текстом, возвращаем false
  if (
    !textObject ||
    (textObject.type.toLowerCase() !== 'textbox' && textObject.type.toLowerCase() !== 'textentity')
  ) {
    return false;
  }

  const isBold = isTextBold(textObject);
  const isUnderlined = isTextUnderlined(textObject);

  // Если хотя бы одно из свойств имеет неоднозначное значение (undefined),
  // то общий результат также неоднозначен
  if (isBold === undefined || isUnderlined === undefined) {
    return undefined;
  }

  // Текст считается обычным, если он не жирный и не подчеркнутый
  return !isBold && !isUnderlined;
};

/**
 * Получает активный цвет из текстового объекта (упрощенная версия)
 * @param textObject - Текстовый объект
 * @returns Цвет текста
 */
export const getActiveColorFromTextObject = (textObject: Textbox | TextEntity) => {
  const activeColor = textObject.get('fill') || textObject.get('stroke');
  return activeColor;
};
